import {ProductTechnicalPropertyTypeIds} from "../../../../../../../../core/constants/enums";
import {PreventionInvalidityCode} from "../preventative-interactions";
import {BaseImportTransformer} from "./shared";

/**
 * @param {string|undefined|null} raw
 * @returns {number|null}
 */
function resolveProductPropertyTypeId(raw) {
    const typeStr = raw?.toString()?.toLowerCase()?.trim() ?? "";
    switch (typeStr) {
        case "text":
            return ProductTechnicalPropertyTypeIds.text;
        case "number":
            return ProductTechnicalPropertyTypeIds.number;
        case "decimal":
            return ProductTechnicalPropertyTypeIds.decimal;
        case "fraction":
            return ProductTechnicalPropertyTypeIds.fraction;
        case "money":
        case "currency":
            return ProductTechnicalPropertyTypeIds.money;
        case "boolean":
        case "true / false":
        case "true/false":
            return ProductTechnicalPropertyTypeIds.boolean;
        case "date":
            return ProductTechnicalPropertyTypeIds.date;
        case "color":
            return ProductTechnicalPropertyTypeIds.color;
        default:
            return null;
    }
}

/**
 * Product properties (per industry) import — phased validation (see {@link BaseImportTransformer} run contract).
 *
 * @param {Array<Object|null|undefined>} mappedData
 * @param {boolean} isFinalRun
 * @param {string|number} industryId
 */
export class ProductPropertiesImportTransformer extends BaseImportTransformer {
    async run(mappedData, isFinalRun = false, industryId) {
        const rows = mappedData ?? [];
        const preventativeIssues = [];

        /** @type {{index: number, item: *, titleTrim: string, typeTrim: string, typeId: number|null, codes: string[]}[]} */
        const rowState = rows.map((item, index) => {
            const titleTrim = item?.title?.trim() ?? "";
            const typeTrim = item?.type?.toString()?.trim() ?? "";
            return {
                index,
                item,
                titleTrim,
                typeTrim,
                typeId: titleTrim && typeTrim ? resolveProductPropertyTypeId(item?.type) : null,
                codes: [],
            };
        });

        // ── Phase 1: required title + type ───────────────────────────────────────
        for (const rs of rowState) {
            if (!rs.item) continue;
            if (!rs.titleTrim || !rs.typeTrim) {
                rs.codes.push(PreventionInvalidityCode.MISSING_REQUIRED_FIELDS);
            }
        }

        // ── Phase 2: known property type (when both cells present) ─────────────────
        for (const rs of rowState) {
            if (!rs.item) continue;
            if (!rs.titleTrim || !rs.typeTrim) continue;
            if (rs.typeId == null) {
                rs.codes.push(PreventionInvalidityCode.MISSING_REQUIRED_FIELDS);
            }
        }

        // ── Emit merged issues ─────────────────────────────────────────────────────
        for (const rs of rowState) {
            if (!rs.item) {
                preventativeIssues.push({
                    rowIndex: rs.index,
                    originalRow: rows[rs.index],
                    invalidityCodes: [PreventionInvalidityCode.MISSING_REQUIRED_FIELDS],
                });
                continue;
            }
            if (!rs.codes.length) continue;
            preventativeIssues.push({
                rowIndex: rs.index,
                originalRow: rs.item,
                invalidityCodes: [...new Set(rs.codes)],
            });
        }

        const issueIndexSet = new Set(preventativeIssues.map((p) => p.rowIndex));

        const apiRequests = rows.map((item, index) => {
            if (!item) {
                return isFinalRun ? null : {title: undefined, type: undefined};
            }

            const rs = rowState[index];
            if (issueIndexSet.has(index)) {
                return isFinalRun
                    ? null
                    : {
                          title: rs.titleTrim || item.title?.trim(),
                          type: item.type?.toString(),
                      };
            }

            return {
                title: rs.titleTrim,
                description: item.description?.trim(),
                industryPropertyTypeId: rs.typeId,
                industryId,
            };
        });

        return {apiRequests, preventativeIssues};
    }
}
